import { useRouter } from 'next/router';
import { useEffect } from 'react';

import { useGetAssociation, useGetGroup } from 'legacy/groups/hooks';
import { useMediaQuery } from 'theme/useMediaQuery';
import { Breakpoints, BreakpointsKeys } from 'theme/types';
import { useGetOrganizationMemberships } from 'api/queries/organizations/memberships';
import { useGetCurrentUserOrganizationsMemberships } from 'api/queries/users/memberships';
import { joinOrganization as joinOrganizationMutation } from 'api/queries/organizations/members';
import { getScreeningQuestionsConfig } from '../../../helpers/screeningQuestionsConfig';

function useViewOrganization() {
  const router = useRouter();
  const { mutate: joinOrganizationCommand } = joinOrganizationMutation();

  // TODO: Manage screen sizes from within a shared hook inside the design system
  const isDesktop = useMediaQuery(
    `(min-width: ${Breakpoints[BreakpointsKeys.Tablet]}px)`
  );

  const {
    data: organization,
    isLoading,
    refetch,
    isError
  } = useGetGroup({
    groupId: router.query.organizationId as string
  });

  const { data: organizationMemberships } = useGetOrganizationMemberships({
    organizationId: router.query.organizationId as string,
    enabled: !!router.query.organizationId
  });

  const { data: currentUserOrganizationMemberships } =
    useGetCurrentUserOrganizationsMemberships({
      enabled: !!router.query.organizationId
    });

  const associationId = organization?.associationDependentId;
  const { data: association } = useGetAssociation({
    associationId: associationId || ''
  });

  const { shouldShowScreeningQuestions, screeningQuestionsFormId } =
    getScreeningQuestionsConfig(organization, association);

  useEffect(() => {
    if (router.query.join === 'true' && organization?.id) {
      const newQuery = { ...router.query };
      delete newQuery.join;
      router.replace(
        {
          pathname: router.pathname,
          query: newQuery
        },
        undefined,
        { shallow: true }
      );

      joinOrganization();
    }
  }, [router.query.join, organization?.id]);

  const handleJoinOrganization = () => {
    if (shouldShowScreeningQuestions) {
      const query = router.query.webview
        ? { webview: router.query.webview }
        : {};
      router.push({
        pathname: `/organizations/${router.query.organizationId}/join/screening`,
        query
      });
    } else {
      joinOrganization();
    }
  };

  const joinOrganization = () => {
    joinOrganizationCommand(
      { organizationId: organization.id },
      { onSuccess: () => refetch() }
    );
  };

  const upcomingMemberships = currentUserOrganizationMemberships?.find(
    (membership) =>
      membership.areaId === organization?.id && membership.status === 'upcoming'
  );

  // Check if joining is blocked by the parent association
  // blockOrganizationMemberJoin = true means all orgs blocked by default
  // unless the org ID is in memberJoinAllowedOrganizationIds (the allowlist)
  const isJoinBlocked = (() => {
    if (!association?.blockOrganizationMemberJoin) {
      return false;
    }
    // If blocking is enabled, check if this org is in the allowlist
    const allowlist = association?.memberJoinAllowedOrganizationIds || [];
    const isInAllowlist =
      organization?.id && allowlist.includes(organization.id);
    return !isInAllowlist;
  })();

  return {
    data: {
      organization,
      isLoading,
      isError,
      isDesktop,
      organizationMemberships,
      upcomingMemberships,
      screeningQuestionsFormId,
      shouldShowScreeningQuestions,
      isJoinBlocked,
      association
    },
    joinOrganization: handleJoinOrganization
  };
}

export default useViewOrganization;
